import giftBoxLottie from "@/GiftboxLottie.json";
import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import { ExtensionLink } from "components/core/ExtensionLink";
import { SearchTextField } from "components/core/inputs/SearchTextField";
import { SignInModal } from "components/core/modals/SignInModal";
import { ChromeGoogleAppMenu } from "components/domain/chrome-extension/ChromeGoogleAppMenu";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "hooks/useAuth";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useWindowSize } from "hooks/useWindowSize";
import Lottie from "lottie-react";
import { MenuIcon, SearchIcon, SearchXIcon, XIcon } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/router";
import {
    ClassAttributes,
    ComponentProps,
    HTMLAttributes,
    MouseEventHandler,
    useEffect,
    useRef,
    useState
} from "react";
import { zIndex } from "styles/zIndex";
import { useOnClickOutside } from "usehooks-ts";

import { Logo } from "../Logo";
import { Overlay, Overlay as SearchBarOverlay } from "../Overlay";
import { ThemeToggle } from "../ThemeToggle";
import TrendingContentsBanner from "../banners/TrendingContentsBanner";
import { Drawer } from "./Drawer";

// Array of fun emojis to randomly select from
const EMOJIS = [
    "🎁",
    "🎄",
    "🎅",
    "❄️",
    "⛄",
    "🦌",
    "🎄",
    "🎊",
    "🎉",
    "✨",
    "🌟",
    "💫",
    "🎈",
    "🎀",
    "🎁",
    "🎄",
    "🎅",
    "🤶",
    "🧑‍🎄",
    "🎄",
    "🎁",
    "🎄",
    "🎅",
    "❄️",
    "⛄",
    "🦌",
    "🎄",
    "🎊",
    "🎉",
    "✨"
];

const AnimatePresenceComponent = AnimatePresence as any;

const FEEDBACK_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSfHxmzWAghuqpRmBWyK2V2Yu1phnjmFpGoYDhMgjtzIoJHTgw/viewform ";

// Custom easing function approximating CSS linear() easing
// Based on: linear(0,0.012_0.9%,0.05_2%,0.411_9.2%,0.517_11.8%,0.611_14.6%,0.694_17.7%,0.765_21.1%,0.824_24.8%,0.872_28.9%,0.91_33.4%,0.939_38.4%,0.977_50.9%,0.994_68.4%,1)
const customEasing = (t: number): number => {
    // Key points from the linear() function
    const points: Array<[number, number]> = [
        [0, 0],
        [0.009, 0.012],
        [0.02, 0.05],
        [0.092, 0.411],
        [0.118, 0.517],
        [0.146, 0.611],
        [0.177, 0.694],
        [0.211, 0.765],
        [0.248, 0.824],
        [0.289, 0.872],
        [0.334, 0.91],
        [0.384, 0.939],
        [0.509, 0.977],
        [0.684, 0.994],
        [1, 1]
    ];

    // Find the two points to interpolate between
    for (let i = 0; i < points.length - 1; i++) {
        const point1 = points[i];
        const point2 = points[i + 1];

        if (!point1 || !point2) continue;

        const [t1, v1] = point1;
        const [t2, v2] = point2;

        if (t >= t1 && t <= t2) {
            // Linear interpolation between the two points
            const ratio = (t - t1) / (t2 - t1);
            return v1 + (v2 - v1) * ratio;
        }
    }

    return t; // Fallback
};

const MenuButton = ({ className, ...props }: ComponentProps<typeof Button>) => {
    return (
        <Button
            className={cn("py-1 lg:hidden [&_svg]:!text-grey-800", className)}
            variant="text"
            color="grey"
            size="medium"
            startIcon="Menu"
            {...props}
        />
    );
};

export const Header = ({
    children,
    showSearch,
    hideAuth = false,
    hideThemeToggle = false,
    type = "default",
    onClose,
    ...props
}: ClassAttributes<HTMLDivElement> &
    HTMLAttributes<HTMLDivElement> & {
        showSearch: boolean;
        hideAuth: boolean;
        hideThemeToggle?: boolean;
        type?: "default" | "transparent" | "border";
        onClose?: MouseEventHandler<HTMLImageElement>;
    }) => {
    const router = useRouter();
    const { me, loading, openSignInModal } = useAuth();
    const { isFeatureEnabled } = useFeatureFlags();
    const { pathname, query, push } = useRouter();

    const profileRef = useRef(null);
    const headerRef = useRef<HTMLElement>(null);
    const { isMobile } = useWindowSize();

    const [showSearchBar, setShowSearchBar] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);
    const [text, setText] = useState("");
    const [showModal, setShowModal] = useState(false);

    const typeStyles = {
        default: {
            backgroundColor: "bg-background",
            borderColor: "border-[var(--color-background)]",
            menuColor: "text-secondary-main",
            logoColor: "text-common-black-main dark:text-common-white-main"
        },
        transparent: {
            backgroundColor: "bg-transparent",
            borderColor: "border-transparent",
            menuColor: "text-common-white-main",
            logoColor: "text-common-white-main"
        },
        border: {
            backgroundColor: "bg-background",
            borderColor: "border-text-focus-visible",
            menuColor: "text-common-black-main dark:text-common-white-main",
            logoColor: "text-common-black-main dark:text-common-white-main"
        }
    }[type];

    useOnClickOutside(headerRef as any, () => setShowSearchBar(false));

    useEffect(() => {
        if (isMobile && pathname !== "search") {
            setShowSearchBar(false);
        }
    }, [isMobile, pathname]);

    /* 검색 페이지에서 벗어날 때 텍스트를 초기화합니다 */
    useEffect(() => {
        if (pathname !== "/search") {
            setText("");
        } else {
            const queryText = query.text;

            if (typeof queryText === "string") {
                setText(queryText);
            }
        }
    }, [pathname, query, setText]);

    /* route change가 시작될 때 Drawer를 닫습니다 */
    useEffect(() => {
        const handler = () => {
            setShowDrawer(false);
        };

        router.events.on("routeChangeStart", handler);

        return () => {
            router.events.off("routeChangeStart", handler);
        };
    }, [router.events, onClose]);

    const { className: propsClassName, ...restProps } = props;

    return (
        <>
            <header
                className={cn(
                    "sticky top-0 z-[100] border-b-[1.5px]",
                    typeStyles.backgroundColor,
                    typeStyles.borderColor,
                    propsClassName
                )}
                ref={headerRef}
                {...restProps}
            >
                <div
                    className="box-border flex h-[var(--header-height)] w-full
                        items-center px-5"
                >
                    <div className="flex items-center gap-3">
                        <MenuIcon
                            className="block size-6 cursor-pointer lg:hidden"
                            onClick={() => setShowDrawer(true)}
                        />

                        <div
                            className="mr-[var(--sidebar-margin)]
                                w-[var(--sidebar-width)] flex-shrink-0 md:mr-0
                                lg:mr-6 lg:w-auto"
                        >
                            <ExtensionLink href="/">
                                <Logo
                                    className={cn(
                                        "text-current",
                                        typeStyles.logoColor
                                    )}
                                />
                            </ExtensionLink>
                        </div>
                    </div>
                    <div
                        className="flex min-w-0 flex-1 items-center justify-end
                            gap-2"
                    >
                        {isFeatureEnabled("CHRISTMAS_EVENT") && (
                            <motion.div
                                onClick={() => {
                                    if (typeof window !== "undefined") {
                                        const currentValue =
                                            localStorage.getItem(
                                                "mix_christmas_cursor"
                                            );
                                        // Toggle cursor: if enabled (has emoji value), disable; if disabled, enable with random emoji
                                        if (
                                            currentValue &&
                                            EMOJIS.includes(currentValue)
                                        ) {
                                            localStorage.removeItem(
                                                "mix_christmas_cursor"
                                            );
                                        } else {
                                            // Randomly select an emoji and store it
                                            const randomEmoji =
                                                EMOJIS[
                                                    Math.floor(
                                                        Math.random() *
                                                            EMOJIS.length
                                                    )
                                                ] || "🎁";
                                            localStorage.setItem(
                                                "mix_christmas_cursor",
                                                randomEmoji
                                            );
                                        }
                                        // Dispatch custom event to update cursor immediately
                                        window.dispatchEvent(
                                            new Event(
                                                "mix_christmas_cursor_changed"
                                            )
                                        );
                                    }
                                }}
                                className="hidden cursor-pointer md:block"
                                whileHover={{
                                    scale: 1.1,
                                    rotate: [0, -5, 5, -5, 0],
                                    transition: {
                                        duration: 0.3,
                                        rotate: {
                                            duration: 0.5
                                        }
                                    }
                                }}
                                whileTap={{
                                    scale: 0.9,
                                    transition: {
                                        duration: 0.1
                                    }
                                }}
                            >
                                <Lottie
                                    animationData={giftBoxLottie}
                                    loop={true}
                                    autoplay={true}
                                    className="size-12"
                                />
                            </motion.div>
                        )}
                        <TrendingContentsBanner />
                        {/* Desktop Only */}
                        {showSearch && (
                            <>
                                <SearchTextField
                                    className="hidden h-9 max-w-[200px]
                                        flex-shrink-0 lg:block"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                />
                            </>
                        )}

                        {/* Tablet & Mobile */}
                        {showSearch && (
                            <button
                                className="flex size-9 items-center
                                    justify-center lg:hidden"
                                onClick={() => setShowSearchBar(!showSearchBar)}
                            >
                                {pathname === "/search" || showSearchBar ? (
                                    <>
                                        <SearchXIcon
                                            size={23}
                                            className="cursor-pointer"
                                        />
                                    </>
                                ) : (
                                    <SearchIcon
                                        size={23}
                                        className="cursor-pointer"
                                    />
                                )}
                            </button>
                        )}

                        {!hideThemeToggle && (
                            <div
                                className="mr-1 flex h-9 flex-shrink-0
                                    items-center justify-center"
                            >
                                <ThemeToggle size="small" />
                            </div>
                        )}

                        {/* Chrome Extension - Google App Menu */}
                        {process.env.BUILD_TARGET === "extension" &&
                        typeof chrome !== "undefined" ? (
                            <div className="flex h-9 items-center">
                                <ChromeGoogleAppMenu />
                            </div>
                        ) : null}

                        {loading ? null : me ? (
                            <ExtensionLink href="/mypage">
                                <div
                                    ref={profileRef}
                                    className="z-[101] hidden h-9 lg:block"
                                >
                                    <Image
                                        alt="profile"
                                        src={me.profileImg}
                                        width={36}
                                        height={36}
                                        className="cursor-pointer rounded-md"
                                    />
                                </div>
                            </ExtensionLink>
                        ) : process.env.BUILD_TARGET === "extension" ? (
                            <div className="flex h-9 items-center">
                                <ExtensionLink href="#signin">
                                    <Button variant="filled" color="secondary">
                                        로그인
                                    </Button>
                                </ExtensionLink>
                            </div>
                        ) : hideAuth ? null : (
                            <div className="flex h-9 items-center">
                                <Button
                                    variant="filled"
                                    color="secondary"
                                    onClick={openSignInModal}
                                >
                                    로그인
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                {(pathname === "/search" || showSearchBar) && (
                    <div
                        className="absolute left-0 top-[var(--header-height)]
                            z-[99] h-[var(--searchbar-height)] w-full
                            bg-subcolor-background px-[var(--header-padding)]
                            pb-[var(--searchbar-padding-bottom)]
                            pt-[var(--searchbar-padding-top)] lg:hidden"
                    >
                        <SearchTextField
                            autoFocus
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </div>
                )}
            </header>
            <AnimatePresenceComponent>
                {showDrawer && (
                    <Overlay
                        className="z-[102] block lg:hidden"
                        initial="hidden"
                        animate={showDrawer ? "visible" : "hidden"}
                        exit="hidden"
                        transition={{
                            ease: customEasing,
                            duration: 0.8
                        }}
                    >
                        <Drawer
                            variants={{
                                visible: {
                                    x: 0
                                },
                                hidden: {
                                    x: "-100%"
                                }
                            }}
                            transition={{
                                ease: customEasing,
                                duration: 0.5
                            }}
                            onClose={() => setShowDrawer(false)}
                        />
                    </Overlay>
                )}
            </AnimatePresenceComponent>
            {pathname !== "/search" && showSearchBar && (
                <SearchBarOverlay className="z-[98] md:hidden" />
            )}
            {showModal && (
                <SignInModal
                    isOpen={showModal}
                    close={async () => setShowModal(false)}
                />
            )}
        </>
    );
};
